import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Upload, Save, RotateCcw, FileText, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import { useSellers } from '../hooks/useSellers';
import { useLots } from '../hooks/useLots';
import { useLotHistory } from '../hooks/useLots';
import { db } from '../firebaseConfig';
import { setDoc, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { useComisiones } from '../hooks/useComisiones';
import { useAlertaCuotasVencidas } from '../hooks/useAlertaCuotasVencidas';
import { AlertaCuotasVencidas } from './ui/AlertaCuotasVencidas';
import { useNotification } from '../context/NotificationContext';

export function LotForm({ setActiveTab }: { setActiveTab: (tab: string) => void }) {
  const { lots } = useLots();
  const { sellers } = useSellers();
  const { addLot } = useLots();
  const { addComision } = useComisiones();
  const { config, addUsuario } = useAppData();
  const getTodayYMD = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localDate = new Date(now.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split('T')[0];
  };
  const [formData, setFormData] = useState({
    numeroLotes: '',
    vendedor: '',
    nombreComprador: '',
    dpiComprador: '',
    telefono: '',
    fechaVenta: getTodayYMD(),
    tipoPago: 'contado',
    valorLote: String(config?.precioBase || 25600),
    valorTotal: '',
    numeroPagos: '',
    boletaDeposito: '', // Mantener para compatibilidad con datos históricos (oculto en UI)
    numeroRecibo: '',
    numeroDocumento: '', // No. Doc. del Banco
    documentos: [],
    enganche: '',
    observaciones: ''
  });
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();
  const [loteExistente, setLoteExistente] = useState<any | null>(null);
  const [vendedorAutoSeleccionado, setVendedorAutoSeleccionado] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertaDismissed, setAlertaDismissed] = useState(false);

  // Hook para detectar cuotas vencidas
  const { tieneVendedorCuotasVencidas, getAlertaVendedor } = useAlertaCuotasVencidas();

  useEffect(() => { }, [sellers]);

  useEffect(() => {
    // Prefill form if editing
    const editLotesRaw = window.sessionStorage.getItem('editLotes');
    if (editLotesRaw && lots.length > 0) {
      try {
        const editLotes = JSON.parse(editLotesRaw);
        if (Array.isArray(editLotes) && editLotes.length > 0) {
          // Buscar los lotes a editar
          const lotesEdit = lots.filter((l: any) => editLotes.includes(l.id));
          if (lotesEdit.length > 0) {
            // Si es edición múltiple, agrupar campos comunes y concatenar IDs
            const primerLote = lotesEdit[0];
            setFormData(prev => ({
              ...prev,
              numeroLotes: lotesEdit.map((l: any) => l.id).join(', '),
              vendedor: lotesEdit.every((l: any) => l.seller === primerLote.seller) ? primerLote.seller : '',
              nombreComprador: lotesEdit.every((l: any) => l.buyerName === primerLote.buyerName) ? primerLote.buyerName : '',
              dpiComprador: lotesEdit.every((l: any) => l.buyerDPI === primerLote.buyerDPI) ? primerLote.buyerDPI : '',
              telefono: lotesEdit.every((l: any) => l.phoneNumber === primerLote.phoneNumber) ? primerLote.phoneNumber : '',
              fechaVenta: lotesEdit.every((l: any) => l.saleDate === primerLote.saleDate) ? primerLote.saleDate : '',
              tipoPago: lotesEdit.every((l: any) => l.paymentType === primerLote.paymentType) ? primerLote.paymentType : 'contado',
              valorLote: '', // Se calcula
              valorTotal: '', // Se calcula
              numeroPagos: lotesEdit.every((l: any) => l.numPayments === primerLote.numPayments) ? (primerLote.numPayments?.toString() || '') : '',
              boletaDeposito: lotesEdit.every((l: any) => l.depositSlip === primerLote.depositSlip) ? (primerLote.depositSlip || '') : '', // Preservar valor histórico
              numeroRecibo: lotesEdit.every((l: any) => l.receiptNumber === primerLote.receiptNumber) ? (primerLote.receiptNumber || '') : '',
              numeroDocumento: lotesEdit.every((l: any) => (l.numeroDocumento || '') === (primerLote.numeroDocumento || '')) ? (primerLote.numeroDocumento || '') : '',
              documentos: [], // No se agrupa
              enganche: lotesEdit.every((l: any) => l.amountPaid === primerLote.amountPaid) ? (primerLote.amountPaid?.toString() || '') : '',
              observaciones: '',
            }));
          }
        }
      } catch { }
      window.sessionStorage.removeItem('editLotes');
    }
  }, [lots]);

  // Cálculo de valor total y enganche sugerido
  const valorLote = parseFloat(formData.valorLote) || config?.precioBase || 25600;
  const lotesArray = formData.numeroLotes.split(',').map(l => l.trim()).filter(Boolean);
  const cantidadLotes = lotesArray.length || 1;
  const valorTotal = valorLote * cantidadLotes;
  // Enganche sugerido: 20% del total o Q. 3,000 por lote, el que sea mayor (ahora es opcional, no obligatorio)
  const engancheMinimo = formData.tipoPago === 'pagos' ? Math.max(valorTotal * 0.2, 3000 * cantidadLotes) : 0;

  // Forzar re-render cuando cambie el número de lotes para actualizar cálculos
  useEffect(() => {
    // Este useEffect fuerza la actualización de los cálculos cuando cambia formData.numeroLotes
  }, [formData.numeroLotes, cantidadLotes, valorTotal]);

  // Actualiza el enganche automáticamente si cambia el número de lotes o el tipo de pago
  useEffect(() => {
    if (formData.tipoPago === 'pagos') {
      // Solo establece el enganche sugerido si está vacío (no forzar el mínimo)
      if (!formData.enganche) {
        setFormData(prev => ({ ...prev, enganche: engancheMinimo.toString() }));
      }
    } else {
      setFormData(prev => ({ ...prev, enganche: '' }));
    }
    // eslint-disable-next-line
  }, [engancheMinimo, formData.tipoPago]);

  // Establecer 12 cuotas por defecto si el tipo de pago es 'pagos' y el campo está vacío
  useEffect(() => {
    if (formData.tipoPago === 'pagos' && (!formData.numeroPagos || formData.numeroPagos === '')) {
      setFormData(prev => ({ ...prev, numeroPagos: '12' }));
    }
  }, [formData.tipoPago]);

  // Preselección automática de vendedor por rango (mejorada para múltiples lotes y rangos)
  const handleNumeroLotesChange = (value: string) => {
    setFormData(prev => ({ ...prev, numeroLotes: value }));
    setError(null);
    setLoteExistente(null);
    // Reset alerta cuando cambia numero de lotes
    setAlertaDismissed(false);

    const lotes = value.split(',').map(l => l.trim()).filter(Boolean);
    if (lotes.length > 0 && sellers.length > 0) {
      // Buscar el primer vendedor que contenga TODOS los lotes en sus rangos
      const vendedorMatch = sellers.find((v: any) => {
        if (!v.ranges || !Array.isArray(v.ranges)) return false;
        return lotes.every((loteId: string) => {
          return v.ranges.some((r: any) => {
            const isNumeric = (str: string) => /^\d+$/.test(str);
            if (isNumeric(r.from) && isNumeric(r.to) && isNumeric(loteId)) {
              const from = parseInt(r.from, 10);
              const to = parseInt(r.to, 10);
              const actual = parseInt(loteId, 10);
              return actual >= from && actual <= to;
            } else {
              // Alfanumérico: "A-1"
              const parseId = (id: string) => {
                const [letra, num] = id.split('-');
                return { letra, num: parseInt(num, 10) };
              };
              try {
                const start = parseId(r.from);
                const end = parseId(r.to);
                const actual = parseId(loteId);
                return (
                  actual.letra === start.letra &&
                  actual.letra === end.letra &&
                  actual.num >= start.num &&
                  actual.num <= end.num
                );
              } catch {
                return false;
              }
            }
          });
        });
      });
      if (vendedorMatch) {
        setFormData(prev => ({ ...prev, vendedor: vendedorMatch.id || '' }));
        setVendedorAutoSeleccionado(true);
      } else {
        setFormData(prev => ({ ...prev, vendedor: '' }));
        setVendedorAutoSeleccionado(false);
      }
    } else {
      setVendedorAutoSeleccionado(false);
    }
  };

  // Validación y registro
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoteExistente(null);
    setIsSubmitting(true);
    try {
      // Validaciones básicas
      // Validar numeroDocumento (campo principal) o boletaDeposito (compatibilidad histórica)
      if (!formData.numeroLotes || !formData.nombreComprador || !formData.dpiComprador || !formData.vendedor || (!formData.numeroDocumento && !formData.boletaDeposito)) {
        setError('Por favor completa todos los campos obligatorios, incluyendo el No. Doc. del Banco.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // Validar que el nombre del comprador solo contenga letras y espacios
      const soloTexto = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/;
      if (!soloTexto.test(formData.nombreComprador.trim())) {
        setError('El nombre del comprador solo puede contener letras y espacios.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Validar cuotas para crédito
      if (formData.tipoPago === 'pagos') {
        const cuotas = parseInt(formData.numeroPagos);
        if (!cuotas || cuotas < 1) {
          setError('Debes ingresar un número de cuotas mayor a 0 para ventas a crédito.');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }

      // Validar que el enganche sea un número válido (permitir cualquier valor, incluso menor al 20%)
      if (formData.tipoPago === 'pagos') {
        const engancheIngresado = parseFloat(formData.enganche || '0');
        if (isNaN(engancheIngresado) || engancheIngresado < 0) {
          setError('El enganche debe ser un valor positivo.');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }

      // Validación de lotes existentes (en memoria y en Firestore)
      for (const loteIdRaw of lotesArray) {
        const loteId = String(loteIdRaw).trim();
        // 1. Buscar si existe como registro individual
        const existente = lots.find((l: any) => String(l.id).trim() === loteId);
        if (existente && (existente.status === 'vendido' || existente.status === 'crédito')) {
          setLoteExistente(existente);
          setError(`El lote ${loteId} ya ha sido vendido o está en crédito.`);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        // 2. Buscar si existe como parte de un grupo (idLotes)
        const grupo = lots.find((l: any) => Array.isArray(l['idLotes']) && l['idLotes'].includes(loteId) && (l.status === 'vendido' || l.status === 'crédito'));
        if (grupo) {
          setLoteExistente(grupo);
          setError(`El lote ${loteId} ya pertenece a un grupo de lotes vendido o en crédito (ID(s) del grupo: ${(grupo as any).idLotes?.join(', ')}). No se puede registrar de nuevo.`);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        // Validación extra en Firestore
        const loteDoc = await getDoc(doc(db, 'lotes', loteId));
        if (loteDoc.exists()) {
          const data = loteDoc.data();
          if (data.status === 'vendido' || data.status === 'crédito') {
            setLoteExistente(data);
            setError(`El lote ${loteId} ya ha sido vendido o está en crédito (verificado en base de datos).`);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
        }
      }

      // Validar lotes fuera de rango
      if (lotesFueraDeRango.length > 0) {
        setError(`No se puede vender el/los lote(s) fuera de rango: ${lotesFueraDeRango.join(', ')}. El máximo permitido es ${config?.lotes}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Determinar status inicial basado en tipo de pago
      const initialStatus: 'vendido' | 'crédito' | 'disponible' | 'devuelto' = formData.tipoPago === 'contado' ? 'vendido' : 'crédito';
      const amountPaid = formData.tipoPago === 'contado' ? valorTotal : parseFloat(formData.enganche || '0');

      // Si es venta de varios lotes, asignar el pago total en un solo payment para el grupo
      let pagosPorLote: any[][] = lotesArray.map(() => []);
      if (formData.tipoPago === 'contado') {
        if (lotesArray.length > 1) {
          // Un solo pago por el total
          pagosPorLote = [[{
            amount: valorTotal,
            date: formData.fechaVenta || new Date().toISOString().split('T')[0],
            type: 'contado',
            method: 'Contado',
            receipt: formData.numeroDocumento || formData.boletaDeposito, // Usar numeroDocumento como principal, boletaDeposito como fallback
            receiptNumber: formData.numeroDocumento || formData.boletaDeposito,
            depositSlip: formData.numeroDocumento || formData.boletaDeposito || formData.numeroRecibo // Preservar compatibilidad
          }]];
        } else {
          pagosPorLote = [[{
            amount: valorLote,
            date: formData.fechaVenta || new Date().toISOString().split('T')[0],
            type: 'contado',
            method: 'Contado',
            receipt: formData.numeroDocumento || formData.boletaDeposito, // Usar numeroDocumento como principal, boletaDeposito como fallback
            receiptNumber: formData.numeroDocumento || formData.boletaDeposito,
            depositSlip: formData.numeroDocumento || formData.boletaDeposito || formData.numeroRecibo // Preservar compatibilidad
          }]];
        }
      } else if (formData.tipoPago === 'pagos' && parseFloat(formData.enganche || '0') > 0) {
        // Asignar el enganche total a cada lote
        pagosPorLote = lotesArray.map(() => [{
          amount: parseFloat(formData.enganche || '0'),
          date: formData.fechaVenta || new Date().toISOString().split('T')[0],
          type: 'enganche',
          method: 'Enganche',
          receipt: formData.numeroRecibo,
          receiptNumber: formData.numeroRecibo,
          depositSlip: formData.boletaDeposito
        }]);
      }

      // Registro de lotes individuales
      const fechaVentaUnica = formData.fechaVenta || new Date().toISOString().split('T')[0];
      const pagosPorLoteGlobal = formData.tipoPago === 'pagos' ? parseInt(formData.numeroPagos) || 0 : 0;
      const valorTotalVenta = valorLote * cantidadLotes;
      // Declarar grupoId una sola vez para todo el flujo
      const grupoId = lotesArray.join('-');
      // --- NUEVO: Crear un solo documento por grupo de lotes ---
      const vendedorSeleccionado = sellers.find((v: any) => v.id === formData.vendedor);
      const nuevoLoteGrupo = {
        id: grupoId,
        idLotes: lotesArray,
        vendedor: formData.vendedor,
        sellerId: vendedorSeleccionado?.id || '',
        sellerName: vendedorSeleccionado?.name || '',
        comprador: formData.nombreComprador,
        dpi: formData.dpiComprador,
        telefono: formData.telefono,
        fechaVenta: fechaVentaUnica,
        tipoVenta: formData.tipoPago === 'contado' ? 'Contado' : 'Crédito',
        numeroCuotas: formData.numeroPagos,
        boletaPago: formData.numeroDocumento || formData.boletaDeposito,
        reciboCaja: formData.numeroRecibo,
        valorLote: valorLote.toString(),
        valorTotal: valorTotalVenta.toString(),
        enganche: formData.enganche,
        abonosRealizados: '0',
        saldoActual: formData.tipoPago === 'contado' ? '0' : (valorTotalVenta - parseFloat(formData.enganche || '0')).toString(),
        status: (formData.tipoPago === 'contado' ? 'vendido' : 'crédito') as 'vendido' | 'crédito',
        seller: formData.vendedor,
        buyerName: formData.nombreComprador,
        buyerDPI: formData.dpiComprador,
        phoneNumber: formData.telefono,
        saleDate: fechaVentaUnica,
        paymentType: formData.tipoPago as 'contado' | 'pagos',
        lotValue: valorLote,
        totalValue: valorTotalVenta,
        amountPaid: formData.tipoPago === 'contado' ? valorTotalVenta : pagosPorLote[0][0]?.amount || 0,
        numberOfPayments: pagosPorLoteGlobal,
        numPayments: pagosPorLoteGlobal, // <-- Campo requerido por addLot para cuotas
        payments: pagosPorLote[0],
        depositSlip: formData.numeroDocumento || formData.boletaDeposito, // Usar numeroDocumento como principal, preservar boletaDeposito histórico
        receiptNumber: formData.numeroRecibo,
        numeroDocumento: formData.numeroDocumento, // Nuevo campo: No. Doc. del Banco
        transferencia: formData.numeroRecibo?.trim() || undefined, // No. Autorización (opcional)
        documents: formData.documentos,
        observaciones: '',
        createdAt: new Date().toISOString(),
      };
      try {
        console.log("Intentando guardar lote en Firestore", nuevoLoteGrupo);
        if (lotesArray.length > 1) {
          // Venta múltiple: crear documento de grupo con ID concatenado
          await setDoc(doc(db, 'lots', grupoId), nuevoLoteGrupo);
          // Eliminar documentos individuales de los lotes involucrados
          for (const loteId of lotesArray) {
            await deleteDoc(doc(db, 'lots', loteId));
          }
        } else {
          // Venta individual: crear documento con ID del lote
          const loteIndividual = { ...nuevoLoteGrupo, id: lotesArray[0] } as any;
          if ('idLotes' in loteIndividual) delete loteIndividual.idLotes;
          await setDoc(doc(db, 'lots', lotesArray[0]), loteIndividual);
        }
        console.log("Lote(s) individuales actualizados exitosamente");
      } catch (e) {
        console.error("Error al guardar lote en Firestore:", e);
      }

      // --- GENERAR CUOTAS PARA VENTA A CRÉDITO (individual o múltiple) ---
      if (formData.tipoPago === 'pagos') {
        const numeroCuotas = parseInt(formData.numeroPagos) || 1;
        const valorTotalGrupo = valorLote * cantidadLotes;
        const engancheTotal = parseFloat(formData.enganche || '0');
        const saldo = valorTotalGrupo - engancheTotal;
        const montoCuota = saldo / numeroCuotas;
        const fechaVenta = fechaVentaUnica;

        // Función helper para añadir meses de forma segura (respeta el día, ajusta cuando no existe)
        const addMonthsSafe = (date: Date, months: number): Date => {
          const result = new Date(date);
          const originalDay = result.getDate();
          const targetMonth = result.getMonth() + months;
          const targetYear = result.getFullYear() + Math.floor(targetMonth / 12);
          const finalMonth = ((targetMonth % 12) + 12) % 12;

          // Setear año y mes primero
          result.setFullYear(targetYear);
          result.setMonth(finalMonth);

          // Obtener el último día del mes de destino
          const lastDayOfTargetMonth = new Date(targetYear, finalMonth + 1, 0).getDate();

          // Si el día original no existe en el mes de destino, usar el último día disponible
          result.setDate(Math.min(originalDay, lastDayOfTargetMonth));

          return result;
        };

        for (let i = 1; i <= numeroCuotas; i++) {
          // La primera cuota es un mes después de la fecha de venta (enganche)
          const fechaVencimiento = addMonthsSafe(new Date(fechaVenta), i);
          const cuotaDoc = {
            cuotaNumero: i,
            fechaVencimiento: fechaVencimiento.toISOString().split('T')[0],
            montoCuota: Number(montoCuota.toFixed(2)),
            estado: 'pendiente',
            comprador: formData.nombreComprador,
            vendedor: vendedorSeleccionado?.id || '',
            vendedorNombre: vendedorSeleccionado?.name || '',
            recibo: formData.numeroRecibo,
            fechaVenta,
            lotes: lotesArray,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          try {
            console.log(`Intentando guardar cuota ${i} en Firestore`, cuotaDoc);
            await setDoc(doc(db, 'cobros', grupoId, 'cuotas', i.toString()), cuotaDoc);
            console.log(`Cuota ${i} guardada exitosamente`);
          } catch (e) {
            console.error(`Error al guardar cuota ${i} en Firestore:`, e);
          }
        }
      }
      // Movimiento y comisión únicos para el grupo
      const movimientoId = `${grupoId}-${formData.tipoPago === 'contado' ? 'contado' : 'enganche'}`;

      // Construir referencia con información completa
      const referencia = formData.numeroRecibo?.trim()
        ? `No. Doc.: ${formData.numeroDocumento}, No. Autorización: ${formData.numeroRecibo}`
        : `No. Doc.: ${formData.numeroDocumento}`;

      const movimiento = {
        id: movimientoId,
        concepto: formData.tipoPago === 'contado'
          ? `Venta de lotes (${lotesArray.join(', ')}) a ${formData.nombreComprador}`
          : `Enganche por venta de lotes (${lotesArray.join(', ')}) a ${formData.nombreComprador}`,
        responsable: formData.vendedor,
        fecha: fechaVentaUnica,
        monto: formData.tipoPago === 'contado' ? valorTotalVenta : parseFloat(formData.enganche || '0'),
        estado: 'Pagado',
        referencia: referencia,
        lotes: lotesArray,
        comprador: formData.nombreComprador,
        vendedor: vendedorSeleccionado?.id || '',
        vendedorNombre: vendedorSeleccionado?.name || '',
        tipo: formData.tipoPago === 'contado' ? 'contado' : 'enganche',
        origen: 'lote',
        automatic: true, // Marcar como movimiento automático
        // Nuevos campos para match con banco
        numeroDocumento: formData.numeroDocumento.trim(), // No. Doc. del banco
        transferencia: formData.numeroRecibo?.trim() || undefined, // No. Autorización (opcional)
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      try {
        console.log("Intentando guardar movimiento en Firestore", movimiento);
        await setDoc(doc(db, 'movimientos', movimientoId), movimiento);
        console.log("Movimiento guardado exitosamente");
      } catch (e) {
        console.error("Error al guardar movimiento en Firestore:", e);
      }
      // Comisión pendiente única para el grupo
      const lotNumbersSorted = lotesArray.map(l => l.toString()).sort();
      const comisionCustomId = 'comision-' + lotNumbersSorted.join('-');
      const comision = {
        sellerId: formData.vendedor,
        sellerName: sellers.find((v: any) => v.id === formData.vendedor)?.name || '',
        lotId: lotesArray[0], // Usar el primer lote como referencia
        lotNumber: lotesArray.join(', '),
        buyerName: formData.nombreComprador,
        saleDate: fechaVentaUnica,
        totalValue: valorLote * lotesArray.length,
        commissionAmount: config?.tipoComision === 'fijo'
          ? (config.montoComisionFijo || 0) * lotesArray.length
          : (valorLote * lotesArray.length) * ((config?.porcentajeComision || 5) / 100),
        commissionPercentage: config?.tipoComision === 'fijo' ? 0 : (config?.porcentajeComision || 5),
        paid: false,
      };
      try {
        console.log("Intentando guardar comisión en Firestore", comision, comisionCustomId);
        await addComision(comision, comisionCustomId);
        console.log("Comisión guardada exitosamente");
      } catch (e) {
        console.error("Error al guardar comisión en Firestore:", e);
      }

      // Registrar en historial
      const now = new Date().toISOString();
      const historyEntries = lotesArray.map(loteId => ({
        timestamp: now,
        lotId: loteId,
        action: 'registro',
        description: `Lote ${loteId} registrado para ${formData.nombreComprador} por ${formData.vendedor}. Valor: Q${valorLote}, Tipo: ${formData.tipoPago}, Estado: ${formData.tipoPago === 'contado' ? 'vendido' : 'crédito'}`,
        details: {
          buyerName: formData.nombreComprador,
          buyerDPI: formData.dpiComprador,
          seller: formData.vendedor,
          lotValue: valorLote,
          totalValue: valorLote,
          paymentType: formData.tipoPago,
          status: formData.tipoPago === 'contado' ? 'vendido' : 'crédito'
        }
      }));

      // --- GENERAR USUARIO COMPRADOR AUTOMÁTICAMENTE (SOLO PARA CRÉDITO) ---
      if (formData.tipoPago === 'pagos') {
        try {
          const pinAleatorio = Math.floor(1000 + Math.random() * 9000).toString();

          const nuevoUsuario: any = {
            nombre: formData.nombreComprador.trim(),
            dpi: formData.dpiComprador.trim(),
            tipo: 'comprador' as const,
            pin: pinAleatorio,
            lotesAsignados: lotesArray,
            activo: true,
            fechaCreacion: new Date().toISOString(),
            creadoPor: 'sistema'
          };

          // Agregar campos opcionales solo si tienen valor
          if (formData.telefono.trim()) {
            nuevoUsuario.telefono = formData.telefono.trim();
          }

          await addUsuario(nuevoUsuario);

          // Mostrar credenciales generadas
          const credenciales = `Lote(s): ${lotesArray.join(', ')} | PIN: ${pinAleatorio}`;
          showNotification(`Lote(s) registrado(s) exitosamente! Usuario comprador creado automáticamente (pago a crédito). Credenciales: ${credenciales}`, 'success');

          // Copiar credenciales al portapapeles
          try {
            await navigator.clipboard.writeText(credenciales);
            console.log('Credenciales copiadas al portapapeles');
          } catch (clipboardError) {
            console.log('No se pudo copiar al portapapeles:', clipboardError);
          }
        } catch (usuarioError) {
          console.error('Error al crear usuario comprador:', usuarioError);
          showNotification('Lote(s) registrado(s) exitosamente! Error al crear usuario comprador automáticamente.', 'warning');
        }
      } else {
        // Venta de contado - no se crea usuario
        showNotification('Lote(s) registrado(s) exitosamente! (Venta de contado - no se requiere acceso al sistema)', 'success');
      }

      // Elimino resetForm para que el usuario decida cuándo limpiar el formulario
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error en registro de lote:', error);
      let errorMsg = 'Ocurrió un error al registrar el lote.';
      if (error instanceof Error) {
        errorMsg += ' Detalle: ' + error.message;
      } else if (typeof error === 'string') {
        errorMsg += ' Detalle: ' + error;
      }
      setError(errorMsg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      numeroLotes: '',
      vendedor: '',
      nombreComprador: '',
      dpiComprador: '',
      telefono: '',
      fechaVenta: getTodayYMD(),
      tipoPago: 'contado',
      valorLote: String(config?.precioBase || 25600),
      valorTotal: '',
      numeroPagos: '',
      boletaDeposito: '',
      numeroRecibo: '',
      numeroDocumento: '',
      documentos: [],
      enganche: '',
      observaciones: ''
    });
    setError(null);
    setLoteExistente(null);
    setVendedorAutoSeleccionado(false);
    setAlertaDismissed(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'vendedor') {
      setVendedorAutoSeleccionado(false);
      // Reset alerta cuando cambia vendedor
      setAlertaDismissed(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    // Handle file drop logic here
  };

  useEffect(() => {
    // Corrección automática para ventas múltiples ya registradas con enganche mal asignado
    // Busca grupos de lotes con mismo comprador, vendedor, fecha y tipo de pago
    const lotesAgrupados: { [key: string]: any[] } = {};
    lots.forEach((l: any) => {
      if (l.paymentType === 'pagos' && l.status === 'crédito') {
        const key = [l.seller, l.buyerName, l.buyerDPI, l.saleDate, l.depositSlip, l.receiptNumber].join('|');
        if (!lotesAgrupados[key]) lotesAgrupados[key] = [];
        lotesAgrupados[key].push(l);
      }
    });
    let cambios = false;
    const nuevosLots = lots.map((l: any) => {
      if (l.paymentType === 'pagos' && l.status === 'crédito') {
        const key = [l.seller, l.buyerName, l.buyerDPI, l.saleDate, l.depositSlip, l.receiptNumber].join('|');
        const grupo = lotesAgrupados[key];
        if (grupo && grupo.length > 1) {
          // Sumar el enganche total del grupo (pagos iniciales)
          const engancheTotal = grupo.reduce((sum, lote) => sum + (lote.payments && lote.payments[0]?.type === 'enganche' ? Number(lote.payments[0].amount) : 0), 0);
          // Si solo el primer lote tiene enganche y los demás amountPaid=0, corregir
          if (engancheTotal > 0 && grupo.filter(lote => (lote.amountPaid || 0) === 0).length === grupo.length - 1) {
            const enganchePorLote = engancheTotal / grupo.length;
            cambios = true;
            return {
              ...l,
              amountPaid: enganchePorLote,
              payments: [{ ...l.payments[0], amount: enganchePorLote }],
            };
          }
        }
      }
      return l;
    });

  }, [lots]);

  useEffect(() => {
    // Corrección automática para ventas múltiples ya registradas con enganche mal asignado
    // Busca grupos de lotes con mismo comprador, vendedor, fecha y tipo de pago
    const lotesAgrupados: { [key: string]: any[] } = {};
    lots.forEach((l: any) => {
      if ((l.paymentType === 'pagos' || l.paymentType === 'contado') && (l.status === 'crédito' || l.status === 'vendido')) {
        // Agrupar por vendedor, comprador, DPI, recibo, tipo de pago (ignorando la fecha)
        const key = [l.seller, l.buyerName, l.buyerDPI, l.depositSlip, l.receiptNumber, l.paymentType].join('|');
        if (!lotesAgrupados[key]) lotesAgrupados[key] = [];
        lotesAgrupados[key].push(l);
      }
    });
    let cambios = false;
    let nuevosLots = [...lots];
    Object.values(lotesAgrupados).forEach((grupo) => {
      if (grupo.length > 1) {
        // Buscar la fecha de venta más frecuente
        const fechas = grupo.map(l => l.saleDate).filter((f: any) => !!f);
        if (fechas.length > 1) {
          const freq: Record<string, number> = {};
          fechas.forEach(f => { freq[f] = (freq[f] || 0) + 1; });
          const fechaMasFrecuente = Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0];
          // Si hay fechas distintas, unificarlas
          if (fechas.some(f => f !== fechaMasFrecuente)) {
            nuevosLots = nuevosLots.map(l =>
              grupo.some(g => g.id === l.id) ? { ...l, saleDate: fechaMasFrecuente } : l
            );
            cambios = true;
          }
        }
      }
    });

  }, [lots]);

  // Validación de lotes fuera de rango
  const lotesFueraDeRango = lotesArray.filter(lote => {
    // Solo números simples, no rangos alfanuméricos
    return lote && !isNaN(Number(lote)) && Number(lote) > Number(config?.lotes || 0);
  });

  // Aviso contextual de devolución previa (buscar devoluciones individuales o grupales)
  const loteIdParaHistorial = formData.numeroLotes.split(',').map(l => l.trim()).filter(Boolean)[0];
  const { history: lotHistory, loading: loadingHistory } = useLotHistory(loteIdParaHistorial);
  // Buscar devoluciones donde lotId sea igual o contenga el número ingresado (para casos grupales)
  const devolucionReciente = lotHistory.find((h: any) => h.action === 'devolución' && (h.lotId === loteIdParaHistorial || (h.lotId && h.lotId.split('-').includes(loteIdParaHistorial))));
  return (
    <div className="space-y-6">
      {/* Mensajes de feedback */}
      {error && (
        <div className="bg-destructive/15 border border-destructive/30 text-destructive px-4 py-3 rounded-lg flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium">{error}</p>
            {loteExistente && (
              <div className="mt-3 p-3 bg-destructive/10 rounded border border-destructive/20">
                <p className="text-sm font-medium mb-2">Información del lote existente:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><strong>Comprador:</strong> {loteExistente.buyerName}</div>
                  <div><strong>Vendedor:</strong> {loteExistente.seller}</div>
                  <div><strong>Valor del lote:</strong> Q{loteExistente.lotValue}</div>
                  <div><strong>Valor total:</strong> Q{loteExistente.totalValue}</div>
                  <div><strong>Pagado:</strong> Q{loteExistente.amountPaid}</div>
                  <div><strong>Estado:</strong>
                    <Badge variant={loteExistente.status === 'vendido' ? 'default' : 'secondary'} className="ml-1">
                      {loteExistente.status}
                    </Badge>
                  </div>
                  {loteExistente.payments && loteExistente.payments.length > 0 && (
                    <div className="col-span-2">
                      <strong>Pagos realizados:</strong> {loteExistente.payments.length}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success message removed, handled by global notification */}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Registro de Lotes</h2>
          <p className="text-muted-foreground mt-1 text-[13px]">Registra la información completa del lote vendido</p>
        </div>
        <div>
          <span
            className="bg-transparent border border-border hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3 cursor-pointer inline-flex items-center"
            onClick={() => setActiveTab('lotes-registrados')}
          >
            Ir a Lotes Registrados →
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información del Lote */}
        <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
          <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
            <CardTitle className="text-foreground flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Información del Lote
            </CardTitle>
            <CardDescription className="text-[13px] text-muted-foreground mt-1">Datos básicos del lote a registrar</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 bg-card rounded-xl pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="numeroLotes">
                  Número de Lote(s) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="numeroLotes"
                  placeholder="Ej. 1, 2, 3"
                  value={formData.numeroLotes}
                  onChange={(e) => {
                    // Permitir solo números y comas
                    const val = e.target.value.replace(/[^0-9,]/g, '');
                    handleNumeroLotesChange(val);
                  }}
                  className="w-full bg-secondary border-input text-foreground placeholder:text-muted-foreground/50 shadow-none transition-colors rounded-md focus:border-ring focus:ring-0"
                />
                <p className="text-sm text-muted-foreground">Solo números separados por comas, ej. 1,2,3</p>
                {/* Estado de los lotes ingresados */}
                {lotesFueraDeRango.length > 0 && (
                  <div className="mt-2 p-2 bg-yellow-100 border border-yellow-400 rounded text-yellow-900 text-sm font-semibold flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-700" />
                    Lote(s) fuera de rango: {lotesFueraDeRango.join(', ')}. No se puede vender un lote fuera del límite configurado ({config?.lotes}).
                  </div>
                )}
                {lotesArray.length > 0 && lotesFueraDeRango.length === 0 && (
                  <div className="bg-secondary border border-border rounded-lg p-4 mt-2">
                    <div className="font-medium mb-2 text-foreground">Estado de los lotes:</div>
                    {lotesArray.map((loteId, idx) => {
                      // Buscar si el lote existe individualmente
                      const existente = lots.find((l: any) => l.id === loteId);
                      // Buscar si el lote está en algún grupo vendido/crédito
                      const grupo = lots.find((l: any) => Array.isArray(l.idLotes) && l.idLotes.includes(loteId) && (l.status === 'vendido' || l.status === 'crédito'));
                      let estado = 'Disponible';
                      let color = 'bg-green-100 text-green-700 border-green-300';
                      if (grupo) {
                        const tipoVentaGrupo = (grupo as any).tipoVenta || (grupo as any).paymentType || grupo.status;
                        let tipo = '';
                        let colorTag = '';
                        if (tipoVentaGrupo && (tipoVentaGrupo.toLowerCase().includes('contado') || tipoVentaGrupo === 'vendido')) {
                          tipo = 'Contado';
                          colorTag = 'bg-blue-100 text-blue-700 border-blue-300';
                        } else if (tipoVentaGrupo && (tipoVentaGrupo.toLowerCase().includes('crédito') || tipoVentaGrupo === 'crédito')) {
                          tipo = 'Crédito';
                          colorTag = 'bg-yellow-100 text-yellow-800 border-yellow-300';
                        }
                        estado = tipo;
                        color = colorTag;
                      } else if (existente) {
                        let tipo = '';
                        let colorTag = '';
                        if (existente.paymentType === 'contado' || existente.status === 'vendido') {
                          tipo = 'Contado';
                          colorTag = 'bg-blue-100 text-blue-700 border-blue-300';
                        } else if (existente.paymentType === 'pagos' || existente.status === 'crédito') {
                          tipo = 'Crédito';
                          colorTag = 'bg-yellow-100 text-yellow-800 border-yellow-300';
                        }
                        if (existente.status === 'vendido' || existente.status === 'crédito') {
                          estado = tipo;
                          color = colorTag;
                        } else if (existente.status === 'devuelto') {
                          estado = 'Devuelto';
                          color = 'bg-blue-100 text-blue-700 border-blue-300';
                        } else if (existente.status === 'disponible') {
                          estado = 'Disponible';
                          color = 'bg-green-100 text-green-700 border-green-300';
                        }
                      }
                      return (
                        <div key={loteId} className="flex items-center gap-2 mb-1 text-sm">
                          <span className="font-mono">Lote {loteId}:</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold ${color}`}>
                            {estado}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendedor">
                  Vendedor <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Select value={formData.vendedor} onValueChange={(value) => handleInputChange('vendedor', value)}>
                    <SelectTrigger className="bg-secondary border-input text-foreground rounded-[6px] shadow-none transition-colors focus:ring-0 focus:border-ring">
                      <SelectValue placeholder="Selecciona un vendedor" className="placeholder-muted-foreground" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border border-border">
                      {sellers.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">No hay vendedores registrados</div>
                      ) : (
                        sellers.map((v: any) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {/* Información de referencia de vendedor autoasignado */}
                {vendedorAutoSeleccionado && formData.vendedor && (
                  <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded flex flex-col gap-1">
                    <span>✓ Vendedor seleccionado automáticamente: <span className="font-bold text-blue-900 dark:text-blue-300">{formData.vendedor}</span></span>
                    {(() => {
                      const vendedor = sellers.find((v: any) => v.id === formData.vendedor);
                      if (vendedor && vendedor.ranges && vendedor.ranges.length > 0) {
                        return (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {vendedor.ranges.map((r: any, idx: number) => (
                              <span key={idx} className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded px-2 py-0.5 text-[11px] font-semibold">{r.from} - {r.to}</span>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

                {/* Alerta de cuotas vencidas */}
                {formData.vendedor && formData.numeroLotes && !alertaDismissed && tieneVendedorCuotasVencidas(formData.vendedor) && (
                  (() => {
                    const alertaInfo = getAlertaVendedor(formData.vendedor);
                    if (!alertaInfo) return null;

                    return (
                      <div className="mt-3">
                        <AlertaCuotasVencidas
                          vendedorNombre={alertaInfo.vendedorNombre}
                          clientesConCuotasVencidas={alertaInfo.clientesConCuotasVencidas}
                          totalCuotasVencidas={alertaInfo.totalCuotasVencidas}
                          totalMontoVencido={alertaInfo.totalMontoVencido}
                          onDismiss={() => setAlertaDismissed(true)}
                        />
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valorLote">Valor del Lote (Q.)</Label>
                <Input
                  id="valorLote"
                  type="text"
                  inputMode="numeric"
                  value={Number(formData.valorLote).toLocaleString()}
                  onChange={(e) => {
                    // Elimina comas para guardar el valor numérico real
                    const val = e.target.value.replace(/,/g, '');
                    if (val === '' || !isNaN(Number(val))) {
                      handleInputChange('valorLote', val);
                    }
                  }}
                  className="bg-secondary border-input text-foreground rounded-md focus:border-ring focus:ring-0 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valorTotal">Valor Total (Q.)</Label>
                <Input id="valorTotal" value={valorTotal.toLocaleString()} readOnly className="bg-secondary border-input text-foreground rounded-md focus:border-ring focus:ring-0 transition-colors opacity-70" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Información del Comprador */}
        <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
          <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
            <CardTitle className="text-foreground">Información del Comprador</CardTitle>
            <CardDescription className="text-[13px] text-muted-foreground mt-1">Datos personales y de contacto del comprador</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 bg-card rounded-xl pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombreComprador">
                  Nombre del Comprador <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="nombreComprador"
                  placeholder="Ej. Juan Pérez"
                  value={formData.nombreComprador}
                  onChange={e => {
                    const value = e.target.value;
                    // Solo permite letras y espacios
                    if (/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]*$/.test(value) || value === "") {
                      handleInputChange('nombreComprador', value);
                    }
                  }}
                  pattern="[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+"
                  title="Solo se permiten letras y espacios"
                  className="bg-secondary border-input text-foreground placeholder:text-muted-foreground/50 rounded-md focus:border-ring focus:ring-0 transition-colors"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dpiComprador">
                  DPI del Comprador <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="dpiComprador"
                  placeholder="Ej. 1234567890123"
                  value={formData.dpiComprador}
                  onChange={e => {
                    // Solo permite números
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('dpiComprador', value);
                  }}
                  inputMode="numeric"
                  pattern="[0-9]+"
                  title="Solo se permiten números"
                  className="bg-secondary border-input text-foreground placeholder:text-muted-foreground/50 rounded-md focus:border-ring focus:ring-0 transition-colors"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefono">Número de Teléfono</Label>
                <Input
                  id="telefono"
                  placeholder="Ej. 50212345678"
                  value={formData.telefono}
                  onChange={e => {
                    // Solo permite números
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    handleInputChange('telefono', value);
                  }}
                  inputMode="numeric"
                  pattern="[0-9]+"
                  title="Solo se permiten números"
                  className="bg-secondary border-input text-foreground placeholder:text-muted-foreground/50 rounded-md focus:border-ring focus:ring-0 transition-colors"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Información de Pago */}
        <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
          <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
            <CardTitle className="text-foreground">Información de Venta</CardTitle>
            <CardDescription className="text-[13px] text-muted-foreground mt-1">Configuración del tipo de pago y documentación</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 bg-card rounded-xl pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tipoPago">Tipo de Pago</Label>
                <Select value={formData.tipoPago} onValueChange={(value) => handleInputChange('tipoPago', value)}>
                  <SelectTrigger className="bg-secondary border-input text-foreground rounded-[6px] shadow-none transition-colors focus:ring-0 focus:border-ring">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border border-border">
                    <SelectItem value="contado">Contado</SelectItem>
                    <SelectItem value="pagos">Pagos</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.tipoPago === 'pagos' && (
                <div className="space-y-2">
                  <Label htmlFor="numeroPagos">Número de Pagos</Label>
                  <Select
                    value={formData.numeroPagos || '12'}
                    onValueChange={(value) => handleInputChange('numeroPagos', String(parseInt(value, 10)))}
                  >
                    <SelectTrigger className="bg-secondary border-input text-foreground rounded-[6px] shadow-none transition-colors focus:ring-0 focus:border-ring">
                      <SelectValue placeholder="Selecciona número de pagos" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border border-border">
                      {[...Array(24)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1} cuota{i === 0 ? '' : 's'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              <div className="space-y-2">
                <Label htmlFor="numeroDocumento">
                  No. Doc. del Banco <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="numeroDocumento"
                  type="number"
                  placeholder="Ej. 9186345"
                  value={formData.numeroDocumento}
                  onChange={(e) => handleInputChange('numeroDocumento', e.target.value.replace(/[^0-9]/g, ''))}
                  className="bg-secondary border-input text-foreground placeholder:text-muted-foreground/50 rounded-md focus:border-ring focus:ring-0 transition-colors"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="numeroRecibo">
                  No. Autorización <span className="text-gray-400">(Opcional)</span>
                </Label>
                <Input
                  id="numeroRecibo"
                  type="number"
                  placeholder="Ej. 123456789"
                  value={formData.numeroRecibo}
                  onChange={(e) => handleInputChange('numeroRecibo', e.target.value.replace(/[^0-9]/g, ''))}
                  className="bg-secondary border-input text-foreground placeholder:text-muted-foreground/50 rounded-md focus:border-ring focus:ring-0 transition-colors"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fechaVenta">Fecha de Venta <span className="text-red-500">*</span></Label>
                <Input
                  id="fechaVenta"
                  type="date"
                  value={formData.fechaVenta}
                  onChange={(e) => handleInputChange('fechaVenta', e.target.value)}
                  className="bg-secondary border-input text-foreground rounded-md focus:border-ring focus:ring-0 transition-colors"
                  required
                />
              </div>
            </div>

            {formData.tipoPago === 'pagos' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="enganche">Enganche Inicial Pagado (Q.) <span className="text-red-500">*</span></Label>
                  <Input
                    id="enganche"
                    type="text"
                    inputMode="numeric"
                    value={Number(formData.enganche).toLocaleString()}
                    onChange={e => {
                      // Elimina comas para guardar el valor numérico real
                      const val = e.target.value.replace(/,/g, '');
                      setFormData(prev => ({ ...prev, enganche: val }));
                    }}
                    className="bg-secondary border-input text-foreground rounded-md focus:border-ring focus:ring-0 transition-colors"
                  />
                  {formData.enganche && parseFloat(formData.enganche) < engancheMinimo && (
                    <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      El enganche es menor al sugerido (20%): Q. {engancheMinimo.toLocaleString()}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Sugerido (20%): Q. {engancheMinimo.toLocaleString()}</p>
                </div>
                {/* Resumen de financiamiento */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
                  <div className="font-semibold text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-2">
                    <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 17l4 4 4-4m0-5V3m-8 4h8" /></svg>
                    Resumen de Financiamiento
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm mb-2">
                    <div><span className="text-muted-foreground">Monto Total:</span><br /><span className="font-bold text-lg text-blue-700 dark:text-blue-400">Q {(valorLote * cantidadLotes).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                    <div><span className="text-muted-foreground">Enganche:</span><br /><span className="font-bold text-lg text-green-600 dark:text-green-400">Q {parseFloat(formData.enganche || '0').toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                    <div><span className="text-muted-foreground">Saldo Pendiente:</span><br /><span className="font-bold text-lg text-orange-600 dark:text-orange-400">Q {((valorLote * cantidadLotes) - parseFloat(formData.enganche || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                    <div><span className="text-muted-foreground">Cuota Mensual:</span><br /><span className="font-bold text-lg text-blue-600 dark:text-blue-400">Q {(((valorLote * cantidadLotes) - parseFloat(formData.enganche || '0')) / (parseInt(formData.numeroPagos || '12') || 12)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                  </div>
                  <div className="text-xs text-blue-700 dark:text-blue-400 font-medium mt-1">
                    Plan de pagos: {formData.numeroPagos || 1} cuota{parseInt(formData.numeroPagos) === 1 ? '' : 's'} mensuales de Q {(((valorLote * cantidadLotes) - parseFloat(formData.enganche || '0')) / (parseInt(formData.numeroPagos || '12') || 12)).toLocaleString(undefined, { minimumFractionDigits: 2 })} cada una
                  </div>
                </div>
              </>
            )}
            {/* Observaciones */}
            <div className="space-y-2">
              <Label htmlFor="observaciones">Observaciones</Label>
              <textarea
                id="observaciones"
                value={formData.observaciones}
                onChange={e => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
                className="w-full bg-secondary border-input text-foreground placeholder:text-muted-foreground/50 shadow-none transition-colors rounded-md focus:border-ring focus:ring-0 min-h-[100px] p-3"
                placeholder="Agrega cualquier observación relevante sobre el pago o la venta..."
              />
            </div>
          </CardContent>
        </Card>



        {/* Botones        {/* Acciones */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={resetForm}
            className="bg-transparent border border-border hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
            disabled={isSubmitting}
          >
            <RotateCcw className="h-3 .5 w-3.5 mr-1.5" />
            Limpiar
          </Button>
          <Button
            type="submit"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
            disabled={isSubmitting}
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {isSubmitting ? 'Registrando...' : 'Registrar Lote(s)'}
          </Button>
        </div>
      </form>
    </div>
  );
}